<template>
  <div
    class="app app--ai"
    :data-theme="app.theme"
    :data-density="app.density"
    :data-sidebar-collapsed="app.sidebarCollapsed ? '1' : '0'"
  >
    <!-- Standalone layouts (DocReader, Login, launch gate) - no app shell -->
    <template v-if="isStandalone">
      <router-view />
      <button
        v-if="showStandaloneAvatar"
        :class="['standalone-avatar', alignStandaloneAvatarToDocRail && 'standalone-avatar--doc-rail']"
        type="button"
        aria-label="Open user profile"
        title="Open user profile"
        @click="ui.openProfile"
      >
        <span>{{ standaloneAvatarInitials }}</span>
      </button>
    </template>

    <!-- Org Dashboard: standalone layout -->
    <template v-else-if="isOrgRoute">
      <OrgShell
        :team="currentTeam"
        @exit="router.push('/app')"
        @open-profile="ui.openProfile"
      >
        <router-view />
      </OrgShell>
    </template>

    <!-- Team creation (no squads yet) -->
    <template v-else-if="isOnboardingRoute">
      <router-view />
    </template>

    <!-- AI Mode workspace -->
    <template v-else>
      <AIModeOverlay
        :team="currentTeam"
        :team-loading="workspaceTeamLoading"
        :role="app.role"
        @close="noop"
        @open-apps="ui.openApps"
        @open-profile="ui.openProfile"
        @open-squad-switcher="ui.openSwitcher"
        @open-signal="drawers.openSignal"
      />
    </template>

    <!-- Overlays -->
    <TeamSwitcher
      v-if="ui.showSwitcher"
      :open="ui.showSwitcher"
      :teams="switcherTeams"
      :current-id="app.currentTeamId"
      :loading="teamsLoading"
      @select="onSelectTeam"
      @close="ui.closeSwitcher"
    />

    <InternalAppsSwitcher
      v-if="ui.showApps"
      :open="ui.showApps"
      @select="onSelectApp"
      @close="ui.closeApps"
    />

    <CommandPalette
      v-if="ui.showCmd"
      @close="ui.closeCmd"
      @navigate="onCmdNavigate"
      @switch-team="onCmdSwitchTeam"
    />

    <SubteamPickerModal
      v-if="picker.open.value"
      :open="picker.open.value"
      @close="picker.closePicker"
      @created="onSubteamCreated"
    />

    <SignalDrawer
      v-if="drawers.signalDrawer"
      :signal="drawers.signalDrawer"
      @close="drawers.closeSignal"
    />

    <ProjectDrawer
      v-if="drawers.projectDrawer"
      :project="drawers.projectDrawer"
      @close="drawers.closeProject"
    />

    <JourneyDrawer
      v-if="drawers.journeyDrawer"
      @close="drawers.closeJourney"
    />

    <UserProfileDrawer
      v-if="ui.showProfile"
      @close="ui.closeProfile"
      @navigate="onProfileNavigate"
    />

    <AskAiDrawer
      v-if="ui.showAskAi"
      @close="ui.closeAskAi"
    />

    <VoiceAgentFab v-if="showVoiceFab" />

    <TweaksPanel
      v-if="ui.showTweaks"
      v-model:theme="app.theme"
      v-model:density="app.density"
      v-model:role="app.role"
      v-model:home-variant="app.homeVariant"
      v-model:radar-variant="app.radarVariant"
      @close="ui.closeTweaks"
    />
  </div>
</template>

<script setup lang="ts">
import { computed, defineAsyncComponent, onMounted, onUnmounted, watch } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { useAppStore, useUIStore, useDrawersStore } from '@/stores'
import { useTeams } from '@/composables/useTeams'
import { useSubteamPicker } from '@/composables/useSubteamPicker'
import type { BackendTeam } from '@teamofone/api-client'
import { useAuth, notifyAuthChanged } from '@/composables/useAuth'
import { setJwt } from '@/services/slackClient'
import { clearSession, clearTokens } from '@/services/auth/session'
import { UNAUTHORIZED_EVENT } from '@/services/auth/unauthorized'
import {
  DEFAULT_AW_DOCS_PATH,
  DEFAULT_TOOL_SELECTOR_PATH,
  isAwDocsLaunch,
  isAwMissionsLaunch,
  readLaunchConfig,
  teamNameToAwMissionsSlug,
  teamScopedAwMissionsPath,
  type AwMissionsSection,
} from '@/config/launch'
import type { Team } from '@/mock/types'

const OrgShell = defineAsyncComponent(() => import('@/modules/shell/components/OrgShell.vue'))
const AIModeOverlay = defineAsyncComponent(() => import('@/modules/ai-mode/components/AIModeOverlay.vue'))
const loadTeamSwitcher = () => import('@/modules/overlays/components/TeamSwitcher.vue')
const TeamSwitcher = defineAsyncComponent(loadTeamSwitcher)
const InternalAppsSwitcher = defineAsyncComponent(() => import('@/modules/overlays/components/InternalAppsSwitcher.vue'))
const CommandPalette = defineAsyncComponent(() => import('@/modules/overlays/components/CommandPalette.vue'))
const TweaksPanel = defineAsyncComponent(() => import('@/modules/overlays/components/TweaksPanel.vue'))
const SignalDrawer = defineAsyncComponent(() => import('@/modules/radar/components/SignalDrawer.vue'))
const ProjectDrawer = defineAsyncComponent(() => import('@/modules/projects/components/ProjectDrawer.vue'))
const JourneyDrawer = defineAsyncComponent(() => import('@/modules/home/components/JourneyDrawer.vue'))
const UserProfileDrawer = defineAsyncComponent(() => import('@/modules/team/components/UserProfileDrawer.vue'))
const AskAiDrawer = defineAsyncComponent(() => import('@/modules/ask-ai/components/AskAiDrawer.vue'))
const VoiceAgentFab = defineAsyncComponent(() => import('@/modules/voice/components/VoiceAgentFab.vue'))
const SubteamPickerModal = defineAsyncComponent(() => import('@/modules/team/components/SubteamPickerModal.vue'))

const DEFAULT_TEAM: Team = {
  id: 'teamofone',
  name: 'Dev.Tools',
  mission: 'Engineering workspace',
  members: 0,
  agents: 0,
  color: '#2970ff',
  letter: 'T',
  oncall: '',
  oncallColor: '#2970ff',
  alerts: 0,
  health: 'healthy',
  slo: 100,
  lead: '',
  services: 0,
  projects: 0,
  joined: '',
}

const router = useRouter()
const route = useRoute()
const app = useAppStore()
const ui = useUIStore()
const drawers = useDrawersStore()
const auth = useAuth()

const noop = (): void => {
  void 0
}

const teamsStore = useTeams()
const { teams: realTeams, loading: teamsLoading } = teamsStore
const picker = useSubteamPicker()

async function onSubteamCreated(team: BackendTeam): Promise<void> {
  // BackendTeam has no `slug`; switchTeam(id) defaults slug = id which is
  // what the existing select handler already does for catalog-created teams.
  app.switchTeam(team.teamId)
  picker.closePicker()
  // Use replace to avoid stacking history entries; do NOT reload the page —
  // the existing switcher select handler reloads (App.vue onSelectTeam), but
  // a fresh BE team is already in the Pinia store via createWithSubteam.
  await router.replace(workspaceSectionPath('team'))
}

const launchConfig = computed(() => readLaunchConfig())
const launchAwDocs = computed(() => isAwDocsLaunch(launchConfig.value))
const launchAwMissions = computed(() => isAwMissionsLaunch(launchConfig.value))

const TEAM_PALETTE = ['#7a5af8', '#2970ff', '#f04438', '#12b76a', '#f79009', '#0ba5ec', '#ee46bc', '#475467']
function hashToColor(s: string): string {
  let h = 0
  for (const ch of s) h = (h * 31 + ch.charCodeAt(0)) & 0xffffffff
  return TEAM_PALETTE[Math.abs(h) % TEAM_PALETTE.length]
}

const switcherTeams = computed<Team[]>(() =>
  realTeams.value.map(
    (t) =>
      ({
        id: t.teamId,
        name: t.name,
        mission: `${t.githubRepos.length} repo${t.githubRepos.length === 1 ? '' : 's'} · ${t.slackChannels.length} channel${t.slackChannels.length === 1 ? '' : 's'}`,
        members: 0,
        agents: 0,
        color: hashToColor(t.teamId),
        letter: (t.name[0] ?? '?').toUpperCase(),
        current: t.teamId === app.currentTeamId,
        alerts: 0,
      }) as Team,
  ),
)

const isOrgRoute = computed(() => route.name === 'org')
const isOnboardingRoute = computed(() => route.name === 'onboarding')
const isStandalone = computed(() => {
  const p = route.path
  return (
    p.startsWith('/too/docs/') ||
    p.startsWith('/too/prs/') ||
    p === '/login' ||
    p === '/tools' ||
    p === '/too/collab' ||
    p === '/too/coming-soon' ||
    /^\/too\/[^/]+\/docs\//.test(p)
  )
})
const showStandaloneAvatar = computed(() =>
  isStandalone.value &&
  route.path !== '/login' &&
  auth.isAuthed.value,
)
const alignStandaloneAvatarToDocRail = computed(() => {
  const p = route.path
  return (
    p.startsWith('/too/docs/') ||
    p.startsWith('/too/prs/') ||
    /^\/too\/[^/]+\/docs\//.test(p)
  )
})
const showVoiceFab = computed(() => false)
const standaloneAvatarInitials = computed(() => {
  const user = auth.user.value
  const raw = user?.name || user?.email || user?.userId || 'You'
  const nameSource = raw.includes('@') ? raw.split('@')[0] : raw
  const parts = nameSource.split(/[\s._-]+/).filter(Boolean)
  const initials = parts
    .slice(0, 2)
    .map((part) => part.charAt(0))
    .join('')
    .toUpperCase()
  return initials || 'Y'
})

const currentTeam = computed(() => {
  const fromReal = switcherTeams.value.find((t) => t.id === app.currentTeamId)
  if (fromReal) return fromReal
  return switcherTeams.value[0] || DEFAULT_TEAM
})
const workspaceTeamLoading = computed(() => {
  if (isStandalone.value || isOnboardingRoute.value) return false
  if (teamsLoading.value) return true
  if (realTeams.value.length === 0) return true
  return !realTeams.value.some((t) => t.teamId === app.currentTeamId)
})

function routeTeamSlug(): string | undefined {
  const rawParam = route.params.teamSlug ?? route.params.teamId
  const param = Array.isArray(rawParam) ? rawParam[0] : rawParam
  if (typeof param === 'string' && param.length > 0) return param
  const queryTeam = route.query.team
  return typeof queryTeam === 'string' && queryTeam.length > 0 ? queryTeam : undefined
}

function teamSlugForName(name: string): string {
  return teamNameToAwMissionsSlug(name)
}

function teamPathSlugForId(teamId: string): string {
  const real = realTeams.value.find((t) => t.teamId === teamId)
  if (real) return teamSlugForName(real.name)
  if (currentTeam.value.id === teamId) return teamSlugForName(currentTeam.value.name)
  return teamNameToAwMissionsSlug(teamId)
}

function currentTeamPathSlug(): string {
  const currentId = app.currentTeamId || currentTeam.value.id
  return teamPathSlugForId(currentId)
}

function preferredWorkspaceTeam(): BackendTeam | undefined {
  return (
    realTeams.value.find((t) => t.teamId === 'highlevel') ||
    realTeams.value.find((t) => t.workspaceKind === 'org_workspace') ||
    realTeams.value[0]
  )
}

function isPlaceholderTeamSlug(slug: string | undefined): boolean {
  return !slug || slug === 'teamofone'
}

function teamIdFromRouteSlug(slug: string | undefined): string | undefined {
  if (!slug) return undefined
  if (isPlaceholderTeamSlug(slug)) return preferredWorkspaceTeam()?.teamId
  const byId = realTeams.value.find((t) => t.teamId === slug)
  if (byId) return byId.teamId
  const byName = realTeams.value.find((t) => teamSlugForName(t.name) === slug)
  if (byName) return byName.teamId
  return undefined
}

function currentAwMissionsSection(): AwMissionsSection | null {
  const section = route.params.section
  const routeSection = Array.isArray(section) ? section[0] : section
  if (
    routeSection === 'team' ||
    routeSection === 'missions' ||
    routeSection === 'radar' ||
    routeSection === 'metrics'
  ) {
    return routeSection
  }
  if (/^\/(?:app|too\/[^/]+)\/missions(?:\/|$)/.test(route.path)) {
    return 'missions'
  }
  const lastSegment = route.path.split('/').filter(Boolean).at(-1)
  if (
    lastSegment === 'team' ||
    lastSegment === 'missions' ||
    lastSegment === 'radar' ||
    lastSegment === 'metrics'
  ) {
    return lastSegment
  }
  return null
}

function workspaceSectionPath(section: AwMissionsSection): string {
  return teamScopedAwMissionsPath(section, currentTeamPathSlug())
}

function awMissionsRoutePath(section: AwMissionsSection, teamId: string): string {
  const base = teamScopedAwMissionsPath(section, teamPathSlugForId(teamId))
  if (section !== 'missions') return base
  const match = route.path.match(/^\/(?:app|too\/[^/]+)\/missions(?<tail>\/.*)?$/)
  return `${base}${match?.groups?.tail ?? ''}`
}

function onNavigate(key: string) {
  let path: string
  if (key === 'tools') path = DEFAULT_TOOL_SELECTOR_PATH
  else if (key === 'home') path = '/app'
  else if (key === 'collab') {
    path = launchAwDocs.value
      ? DEFAULT_AW_DOCS_PATH
      : '/app/docs'
  } else if (key === 'radar') path = workspaceSectionPath('radar')
  else if (key === 'gates' || key === 'ship') path = '/app/ship'
  else if (key === 'team') path = workspaceSectionPath('team')
  else if (key === 'metrics') path = `/too/${encodeURIComponent(currentTeamPathSlug())}/metrics`
  else if (key === 'missions' || key === 'routines') path = workspaceSectionPath('missions')
  else if (key === 'design') path = '/app/design'
  else path = '/app'
  void router.push(path)
  ui.closeCmd()
}

function onCmdNavigate(key: string) {
  onNavigate(key)
}

function onCmdSwitchTeam() {
  ui.closeCmd()
  ui.openSwitcher()
}

async function onSelectTeam(id: string) {
  const slug = teamPathSlugForId(id)
  app.switchTeam(id, slug)
  ui.closeSwitcher()
  const section = currentAwMissionsSection()
  if (section) {
    await router.replace(awMissionsRoutePath(section, id))
  } else {
    await router.replace({ query: { ...route.query, team: id } })
  }
  window.location.reload()
}

function syncCurrentTeamSlug(): void {
  const id = app.currentTeamId
  if (!id) return
  const slug = teamPathSlugForId(id)
  if (slug && slug !== app.currentTeamSlug) app.setCurrentTeamSlug(slug)
}

function syncTeamFromRoute(): string | undefined {
  const routeSlug = routeTeamSlug()
  const teamId = teamIdFromRouteSlug(routeSlug)
  if (teamId && teamId !== app.currentTeamId) {
    app.switchTeam(teamId, teamPathSlugForId(teamId))
  }
  syncCurrentTeamSlug()
  return teamId
}

function syncAwMissionsRouteSlug(teamId: string): void {
  const section = currentAwMissionsSection()
  if (!section) return
  const routeSlug = routeTeamSlug()
  if (!launchAwMissions.value && !isPlaceholderTeamSlug(routeSlug)) return
  const canonicalSlug = teamPathSlugForId(teamId)
  if (!routeSlug || routeSlug === canonicalSlug) return
  void router.replace({
    path: awMissionsRoutePath(section, teamId),
    query: route.query,
    hash: route.hash,
  })
}

function onSelectApp(id: string) {
  ui.closeApps()
  if (id === 'awdocs') {
    void router.push(DEFAULT_AW_DOCS_PATH)
  } else if (id === 'knowledge') {
    void router.push('/app/knowledge')
  }
}

function onProfileNavigate(key: string) {
  ui.closeProfile()
  onNavigate(key)
}

function isPrimaryShortcut(e: KeyboardEvent, key: string): boolean {
  return (e.metaKey || e.ctrlKey) && e.key.toLowerCase() === key
}

function closeTopOverlay(): void {
  const closeHandlers: Array<[boolean, () => void]> = [
    [ui.showCmd, ui.closeCmd],
    [ui.showSwitcher, ui.closeSwitcher],
    [ui.showApps, ui.closeApps],
    [Boolean(drawers.signalDrawer), drawers.closeSignal],
    [Boolean(drawers.projectDrawer), drawers.closeProject],
    [Boolean(drawers.journeyDrawer), drawers.closeJourney],
    [ui.showProfile, ui.closeProfile],
  ]
  closeHandlers.find(([open]) => open)?.[1]()
}

function onKey(e: KeyboardEvent) {
  if (isPrimaryShortcut(e, 'k')) {
    e.preventDefault()
    if (ui.showCmd) ui.closeCmd()
    else ui.openCmd()
    return
  }
  if (isPrimaryShortcut(e, 's') && e.shiftKey) {
    e.preventDefault()
    ui.openSwitcher()
    return
  }
  if (isPrimaryShortcut(e, 'a') && e.shiftKey) {
    e.preventDefault()
    app.toggleAiMode()
    return
  }
  if (e.key === 'Escape') {
    closeTopOverlay()
  }
}

function onMessage(e: MessageEvent) {
  const d = e.data as { type?: string } | null
  if (!d || typeof d !== 'object') return
  if (d.type === '__activate_edit_mode') {
    app.setEditMode(true)
    ui.openTweaks()
  } else if (d.type === '__deactivate_edit_mode') {
    app.setEditMode(false)
    ui.closeTweaks()
  }
}

watch(
  realTeams,
  (next) => {
    if (next.length === 0 || isStandalone.value) return
    const routeTeamId = syncTeamFromRoute()
    if (routeTeamId) {
      syncAwMissionsRouteSlug(routeTeamId)
      return
    }
    const matchesReal = next.some((t) => t.teamId === app.currentTeamId)
    if (!matchesReal) {
      const target = preferredWorkspaceTeam()
      if (!target) return
      app.switchTeam(target.teamId, teamSlugForName(target.name))
      // If the user just landed their first squad (typically via the
      // SubteamPickerModal opened from /too/onboarding), navigate into the
      // workspace instead of reloading. A full page reload races against
      // onSubteamCreated's own router.replace and the picker's emit cycle,
      // and (because Vue's pre-flush watchers run before the picker's await
      // continuation) it fires while currentTeamId is still unset — which
      // leaves the user stranded on /too/onboarding after the reload.
      if (isOnboardingRoute.value) {
        void router.replace(workspaceSectionPath('team'))
        return
      }
      window.location.reload()
    } else {
      syncCurrentTeamSlug()
    }
  },
  { immediate: true },
)

watch(
  () => route.fullPath,
  () => {
    const teamId = syncTeamFromRoute()
    if (teamId) {
      syncAwMissionsRouteSlug(teamId)
    }
  },
  { immediate: true },
)

const onUnauthorized = () => {
  if (auth.authMode === 'dev') return
  clearSession()
  clearTokens()
  setJwt(null)
  notifyAuthChanged()
  if (route.path !== '/login' && route.path !== '/auth/callback') {
    void router.push({ path: '/login', query: { returnTo: route.fullPath } })
  }
}

onMounted(() => {
  void loadTeamSwitcher()
  if (realTeams.value.length === 0 && !teamsLoading.value) {
    void teamsStore.refresh()
  }
  const teamId = syncTeamFromRoute()
  if (teamId) {
    syncAwMissionsRouteSlug(teamId)
  }
  window.addEventListener('keydown', onKey)
  window.addEventListener('message', onMessage)
  window.addEventListener(UNAUTHORIZED_EVENT, onUnauthorized)
  window.parent.postMessage({ type: '__edit_mode_available' }, '*')
})

onUnmounted(() => {
  window.removeEventListener('keydown', onKey)
  window.removeEventListener('message', onMessage)
  window.removeEventListener(UNAUTHORIZED_EVENT, onUnauthorized)
})
</script>

<style scoped>
.standalone-avatar {
  position: fixed;
  left: max(18px, env(safe-area-inset-left));
  bottom: max(18px, env(safe-area-inset-bottom));
  z-index: 420;
  width: 36px;
  height: 36px;
  display: inline-grid;
  place-items: center;
  border: 0;
  border-radius: 999px;
  background: var(--primary-600);
  color: #ffffff;
  box-shadow: none;
  cursor: pointer;
}

.standalone-avatar--doc-rail {
  left: max(14px, env(safe-area-inset-left));
  bottom: max(24px, env(safe-area-inset-bottom));
  width: 36px;
  height: 36px;
}

.standalone-avatar span {
  font: 700 12px/1 "Inter", "Poppins", sans-serif;
  letter-spacing: 0;
}

.standalone-avatar--doc-rail span {
  font-size: 12px;
}

.standalone-avatar:hover {
  transform: translateY(-1px);
  box-shadow: none;
}

.standalone-avatar:focus-visible {
  outline: 3px solid color-mix(in srgb, var(--primary-400) 35%, transparent);
  outline-offset: 3px;
}
</style>
